import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { PostTradeService } from '../../post-trade.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import {
  directAccountInfoKey,
  directAccountHolderKey,
  shellAccountInfoKey,
  shellAccountHolderKey,
  linkStatusKey,
  linkHistoryKey,
} from './account-linking-detail-keys';
import { MatDialog } from '@angular/material/dialog';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { CardInfopointComponent } from '../../components/card-infopoint/card-infopoint.component';
import { ExpandingTableComponent } from '../../components/expanding-table/expanding-table.component';
import { MatButtonModule } from '@angular/material/button';
import { SearchAccountGridComponent } from '../search-account-grid/search-account-grid.component';
import { NgxLoadingModule } from 'ngx-loading';

@Component({
  selector: 'app-account-linking-detail',
  templateUrl: './account-linking-detail.component.html',
  styleUrls: ['./account-linking-detail.component.scss'],
  imports: [
    MatIconModule,
    MatTabsModule,
    CardInfopointComponent,
    ExpandingTableComponent,
    MatButtonModule,
    SearchAccountGridComponent,
    CommonModule,
    NgxLoadingModule,
  ],
})
export class AccountLinkingDetailComponent implements OnInit, OnDestroy {
  fb = inject(FormBuilder);
  ptSvc = inject(PostTradeService);
  ss = inject(SessionStorageService);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private route = inject(Router);

  @Input() directAccountId: string = '';

  rights = [];
  unsubscribe: Subject<any> = new Subject();
  loading = false;

  directAccountInfoKey = directAccountInfoKey;
  directAccountHolderKey = directAccountHolderKey;
  shellAccountInfoKey = shellAccountInfoKey;
  shellAccountHolderKey = shellAccountHolderKey;
  linkStatusKey = linkStatusKey;
  linkHistoryKey = linkHistoryKey;

  linkedAccountInfo;
  directAccountTab = 0;
  linkedAccountTab = 0;
  defaultSearchIds: Array<string> = [];

  ngOnInit(): void {
    this.loading = true;
    this.rights = this.ss.get('rights');

    this.getLinkDetails();
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getLinkDetails() {
    this.ptSvc
      .getAccountLinkingDetails(this.directAccountId)
      .subscribe(resp => {
        if (resp) {
          this.linkedAccountInfo = resp;

          if (resp.LinkedAccountScores && resp.LinkedAccountScores.length > 0) {
            resp.LinkedAccountScores.map(score => {
              this.defaultSearchIds.push(score.AccountID);
            });
          }
        }

        this.loading = false;
      });
  }

  findMatchScore(id) {
    if (id) {
      const scoreObject = this.linkedAccountInfo.LinkedAccountScores.find(
        account => account.AccountID === id
      );

      if (scoreObject) {
        return scoreObject.Score;
      }
    }
    return 0;
  }

  linkAccount(status) {
    let message = 'Are you sure you would like to link this account?';

    if (status === 'I') {
      message =
        'Are you sure you would like to ignore this shell account link?';
    } else if (status === 'U') {
      message =
        'Are you sure you would like to set this link status to "Unlinked"?';
    }

    const dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content: message,
        confirm: 'Yes',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.loading = true;
        const payload = {
          directID: this.linkedAccountInfo.DirectAccount.DirectAccountID,
          status: status,
        };

        if (!['U', 'I'].includes(status)) {
          payload['shellID'] = this.linkedAccountInfo.TradeAccount.AccountID;
        }

        this.ptSvc.updateLinkedAccount(payload).subscribe(resp => {
          this.getLinkDetails();
        });
      }
    });
  }

  setShellAccount({ AccountID }) {
    this.ptSvc.getShellAccountDetails(AccountID).subscribe(resp => {
      if (resp) {
        this.linkedAccountInfo.LinkedAccount.Status = 'S';

        if (resp.TradeAccount) {
          this.linkedAccountInfo.TradeAccount = resp.TradeAccount;
        }
        if (resp.TradeEntities) {
          this.linkedAccountInfo.TradeEntities = resp.TradeEntities;
        }
        if (resp.TradeEntityAccounts) {
          this.linkedAccountInfo.TradeEntityAccounts = resp.TradeEntityAccounts;
        }
      }
    });
  }

  clearShellAccount() {
    const dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content:
          'Are you sure you would like to clear the shell account details?',
        confirm: 'Yes',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.linkedAccountInfo.LinkedAccount.Status = 'U';

        if (this.linkedAccountInfo.TradeAccount) {
          delete this.linkedAccountInfo.TradeAccount;
        }
      }
    });
  }

  nav(location: string, external = false) {
    if (external) {
      window.open(
        `${this.location.prepareExternalUrl(`/${location}`)}`,
        '_blank'
      );
    } else {
      this.route.navigate([`/${location}`]);
    }
  }
}
